export const TRANSFORMERS_CDN_URL =
  "https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.3.3/+esm";
export const PRIMARY_WHISPER_MODEL_ID =
  "onnx-community/nb-whisper-base-ONNX";
export const FALLBACK_WHISPER_MODEL_IDS = [
  "onnx-community/nb-whisper-tiny-ONNX",
  "NbAiLab/nb-whisper-tiny",
];
export const WHISPER_SAMPLE_RATE = 16000;

let cachedPipelines = {};

export const resetWhisperEngineCache = () => {
  cachedPipelines = {};
};

export const defaultTransformersLoader = () =>
  import(/* webpackIgnore: true */ TRANSFORMERS_CDN_URL);

/**
 * Resolves candidate media URLs for Dimu IDs, HLS playlists, or direct URLs.
 */
export function resolveTranscribableMediaUrls(mediaInput) {
  if (!mediaInput || typeof mediaInput !== "string") {
    return [];
  }
  const trimmed = mediaInput.trim();
  if (!trimmed) {
    return [];
  }

  // Reject dangerous schemes or protocol-relative URLs
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith(["java", "script:"].join("")) ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:") ||
    lower.startsWith("file:") ||
    lower.startsWith("//")
  ) {
    return [];
  }

  // If already an HLS URL or adaptive streaming URL, return only the HLS URL
  // (Prevents requesting /file/{id} on Dimu which returns HTTP 500 for videos)
  if (lower.includes(".m3u8") || lower.includes("/streaming/adaptive/")) {
    return [trimmed];
  }

  // Check if ems.dimu.org file URL (typically audio tracks)
  const dimuFileMatch = trimmed.match(
    /ems\.dimu\.org\/file\/([a-zA-Z0-9_-]+)/
  );
  if (dimuFileMatch) {
    const id = dimuFileMatch[1];
    return [
      `https://ems.dimu.org/file/${id}`,
      `https://ems.dimu.org/streaming/adaptive/${id}/index.m3u8`,
    ];
  }

  // Check if raw Dimu identifier without protocol or slashes
  const isBareDimuId =
    /^[a-zA-Z0-9_-]+$/.test(trimmed) &&
    !trimmed.includes("/") &&
    !trimmed.includes(".");

  if (isBareDimuId) {
    return [
      `https://ems.dimu.org/file/${trimmed}`,
      `https://ems.dimu.org/streaming/adaptive/${trimmed}/index.m3u8`,
    ];
  }

  // Allow safe HTTPS, local dev HTTP, blob, or safe relative paths
  if (
    lower.startsWith("https://") ||
    lower.startsWith("http://localhost") ||
    lower.startsWith("http://127.0.0.1") ||
    lower.startsWith("blob:") ||
    (lower.startsWith("/") && !lower.startsWith("//"))
  ) {
    return [trimmed];
  }

  // Reject all other unverified or dangerous schemes
  return [];
}

/**
 * Parses HLS playlist content and extracts segment URIs.
 * Handles Dimu master playlists with trailing query params like ".m3u8?".
 */
export function parseHlsSegments(manifestText) {
  const lines = manifestText.split(/\r?\n/);
  const segments = [];
  let isVariantPlaylist = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    if (line.startsWith("#EXT-X-STREAM-INF") || /\.m3u8(\?.*)?$/i.test(line)) {
      isVariantPlaylist = true;
    }
    if (!line.startsWith("#")) {
      segments.push(line);
    }
  }

  return { isVariantPlaylist, segments };
}

/**
 * Extracts raw ADTS AAC audio payload from 188-byte MPEG-2 Transport Stream (MPEG-TS) packets.
 * Browser AudioContext cannot decode raw .ts containers; demuxing the audio stream allows
 * native AAC decoding via decodeAudioData().
 */
export function extractAdtsFromMpegTs(uint8) {
  if (!isMpegTs(uint8)) {
    return uint8;
  }

  const demuxer = {
    audioPid: null,
    pendingPusiPid: null,
    pesHeaderBytes: [],
    pesBytesToSkip: 0,
    audioChunks: [],
  };

  for (let i = 0; i + 188 <= uint8.length; i += 188) {
    const packet = parseTsPacketHeader(uint8, i);
    if (!packet) {
      continue;
    }
    if (packet.pusi) {
      processPusiPacket(demuxer, packet, uint8);
    } else {
      processNonPusiPacket(demuxer, packet, uint8);
    }
  }

  if (demuxer.audioChunks.length === 0) {
    return uint8;
  }

  return mergeAudioChunks(demuxer.audioChunks);
}

function isMpegTs(uint8) {
  if (!uint8 || uint8.length < 188) {
    return false;
  }
  if (uint8[0] !== 0x47) {
    return false;
  }
  if (uint8.length >= 376 && uint8[188] !== 0x47) {
    return false;
  }
  return true;
}

function parseTsPacketHeader(uint8, packetStart) {
  if (uint8[packetStart] !== 0x47) {
    return null;
  }
  const b1 = uint8[packetStart + 1];
  const b3 = uint8[packetStart + 3];
  const afc = (b3 >> 4) & 0x03;
  if (afc === 0 || afc === 2) {
    return null;
  }

  let offset = packetStart + 4;
  if (afc === 3) {
    offset += 1 + uint8[packetStart + 4];
  }
  const packetEnd = packetStart + 188;
  if (offset >= packetEnd) {
    return null;
  }

  const pusi = (b1 & 0x40) !== 0;
  const pid = ((b1 & 0x1f) << 8) | uint8[packetStart + 2];
  return { pusi, pid, offset, packetEnd };
}

function hasPesStartCode(uint8, offset, packetEnd) {
  return (
    packetEnd - offset >= 3 &&
    uint8[offset] === 0x00 &&
    uint8[offset + 1] === 0x00 &&
    uint8[offset + 2] === 0x01
  );
}

function isAudioStreamId(streamId) {
  return streamId >= 0xc0 && streamId <= 0xdf;
}

function handleCompletePesHeader(demuxer, uint8, offset, packetEnd, pid) {
  const streamId = uint8[offset + 3];
  if (!isAudioStreamId(streamId)) {
    return;
  }
  demuxer.audioPid = pid;
  const totalHeader = 9 + uint8[offset + 8];
  const avail = packetEnd - offset;
  const skip = Math.min(totalHeader, avail);
  const audioStart = offset + skip;
  demuxer.pesBytesToSkip = totalHeader - skip;
  if (audioStart < packetEnd) {
    demuxer.audioChunks.push(uint8.subarray(audioStart, packetEnd));
  }
}

function handlePartialPesHeader(demuxer, uint8, offset, packetEnd, pid) {
  for (let j = offset; j < packetEnd; j++) {
    demuxer.pesHeaderBytes.push(uint8[j]);
  }
  demuxer.pendingPusiPid = pid;
  if (demuxer.pesHeaderBytes.length >= 4) {
    if (isAudioStreamId(demuxer.pesHeaderBytes[3])) {
      demuxer.audioPid = pid;
    }
  }
}

function processPusiPacket(demuxer, packet, uint8) {
  const { offset, pid, packetEnd } = packet;

  if (!hasPesStartCode(uint8, offset, packetEnd)) {
    return;
  }

  demuxer.pesHeaderBytes = [];
  demuxer.pesBytesToSkip = 0;

  const avail = packetEnd - offset;
  if (avail >= 9) {
    handleCompletePesHeader(demuxer, uint8, offset, packetEnd, pid);
    return;
  }

  handlePartialPesHeader(demuxer, uint8, offset, packetEnd, pid);
}

function resolvePendingPesHeader(demuxer, uint8, offset, packetEnd, pid) {
  const needed = 9 - demuxer.pesHeaderBytes.length;
  const take = Math.min(needed, packetEnd - offset);
  for (let j = 0; j < take; j++) {
    demuxer.pesHeaderBytes.push(uint8[offset + j]);
  }
  const newOffset = offset + take;

  if (demuxer.pesHeaderBytes.length >= 9) {
    const streamId = demuxer.pesHeaderBytes[3];
    if (isAudioStreamId(streamId)) {
      demuxer.audioPid = pid;
      demuxer.pesBytesToSkip = demuxer.pesHeaderBytes[8];
    }
    demuxer.pesHeaderBytes = [];
  }
  return newOffset;
}

function processNonPusiPacket(demuxer, packet, uint8) {
  let { offset } = packet;
  const { pid, packetEnd } = packet;

  if (demuxer.pesHeaderBytes.length > 0 && pid === demuxer.pendingPusiPid) {
    offset = resolvePendingPesHeader(demuxer, uint8, offset, packetEnd, pid);
  }

  if (demuxer.audioPid === null || pid !== demuxer.audioPid) {
    return;
  }

  if (demuxer.pesBytesToSkip > 0) {
    const skip = Math.min(demuxer.pesBytesToSkip, packetEnd - offset);
    offset += skip;
    demuxer.pesBytesToSkip -= skip;
  }

  if (offset < packetEnd) {
    demuxer.audioChunks.push(uint8.subarray(offset, packetEnd));
  }
}

function mergeAudioChunks(chunks) {
  const totalLength = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const result = new Uint8Array(totalLength);
  let cur = 0;
  for (const chunk of chunks) {
    result.set(chunk, cur);
    cur += chunk.byteLength;
  }
  return result;
}

/**
 * Fetches media audio buffer from direct URL or HLS stream.
 */
export async function fetchMediaAudioBuffer(mediaInput, options = {}) {
  const {
    fetchFn = typeof window !== "undefined" && window.fetch
      ? window.fetch.bind(window)
      : fetch,
    signal = null,
    onProgress = null,
    maxSegments = 600,
  } = options;

  const candidateUrls = resolveTranscribableMediaUrls(mediaInput);
  if (candidateUrls.length === 0) {
    throw new Error("Ingen gyldig mediekilde oppgitt");
  }

  let lastError = null;

  for (const url of candidateUrls) {
    if (signal?.aborted) {
      throw new Error("Transkripsjon avbrutt");
    }

    try {
      const resp = await fetchFn(url, { signal, headers: { Accept: "*/*" } });
      if (!resp.ok) {
        lastError = new Error(`HTTP ${resp.status} ved henting av ${url}`);
        continue;
      }

      // Check if this is an HLS playlist by URL or content
      const isM3u8Url = url.toLowerCase().includes(".m3u8");
      if (isM3u8Url) {
        const text = await resp.text();
        return await downloadHlsStream(text, url, {
          fetchFn,
          signal,
          onProgress,
          maxSegments,
        });
      }

      const buffer = await resp.arrayBuffer();
      // Inspect first few bytes: if #EXTM3U was returned without .m3u8 in URL
      const textPreview = new TextDecoder("utf-8").decode(
        new Uint8Array(buffer.slice(0, 7))
      );
      if (textPreview.startsWith("#EXTM3U")) {
        const fullText = new TextDecoder("utf-8").decode(new Uint8Array(buffer));
        return await downloadHlsStream(fullText, url, {
          fetchFn,
          signal,
          onProgress,
          maxSegments,
        });
      }

      const uint8 = new Uint8Array(buffer);
      const demuxed = extractAdtsFromMpegTs(uint8);
      if (demuxed === uint8) {
        return buffer;
      }
      return demuxed.buffer.slice(
        demuxed.byteOffset,
        demuxed.byteOffset + demuxed.byteLength
      );
    } catch (err) {
      if (err.message === "Transkripsjon avbrutt") {
        throw err;
      }
      lastError = err;
    }
  }

  throw (
    lastError ||
    new Error(
      "Kunne ikke hente lyd fra videoen eller lydsporet. Kontroller at filen er tilgjengelig."
    )
  );
}

/**
 * Downloads and concatenates HLS media segments.
 */
async function downloadHlsStream(
  manifestText,
  playlistUrl,
  { fetchFn, signal, onProgress, maxSegments }
) {
  let { isVariantPlaylist, segments } = parseHlsSegments(manifestText);

  // If master playlist with variant playlists, resolve first variant
  if (isVariantPlaylist && segments.length > 0) {
    const resolvedVariant = new URL(segments[0], playlistUrl);
    if (!["https:", "http:"].includes(resolvedVariant.protocol)) {
      throw new Error(`Ugyldig protokoll for HLS-spilleliste: ${resolvedVariant.protocol}`);
    }
    const variantUrl = resolvedVariant.toString();
    const variantResp = await fetchFn(variantUrl, { signal });
    if (!variantResp.ok) {
      throw new Error(`Kunne ikke hente HLS-mediaspilleliste: ${variantUrl}`);
    }
    const variantText = await variantResp.text();
    playlistUrl = variantUrl;
    segments = parseHlsSegments(variantText).segments;
  }

  const selectedSegments = segments.slice(0, maxSegments);
  if (selectedSegments.length === 0) {
    throw new Error("Ingen mediesegmenter funnet i HLS-spillelisten");
  }

  const chunks = [];
  for (let i = 0; i < selectedSegments.length; i++) {
    if (signal?.aborted) {
      throw new Error("Transkripsjon avbrutt");
    }
    const resolvedSeg = new URL(selectedSegments[i], playlistUrl);
    if (!["https:", "http:"].includes(resolvedSeg.protocol)) {
      throw new Error(`Ugyldig protokoll for HLS-segment: ${resolvedSeg.protocol}`);
    }
    const segUrl = resolvedSeg.toString();
    const segResp = await fetchFn(segUrl, { signal });
    if (!segResp.ok) {
      throw new Error(`Kunne ikke hente mediesegment: ${segUrl}`);
    }
    const segBuf = await segResp.arrayBuffer();
    chunks.push(new Uint8Array(segBuf));

    if (onProgress) {
      const pct = Math.round(((i + 1) / selectedSegments.length) * 20);
      onProgress({
        status: `Henter lydspor... (${i + 1}/${selectedSegments.length})`,
        progress: pct,
        stage: "fetch",
      });
    }
  }

  const totalLength = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }

  const demuxed = extractAdtsFromMpegTs(merged);
  return demuxed.buffer.slice(
    demuxed.byteOffset,
    demuxed.byteOffset + demuxed.byteLength
  );
}

/**
 * Downmixes multi-channel audio to mono and linearly resamples to target sample rate.
 */
export function resampleLinearTo16kHzMono(
  audioBuffer,
  targetSampleRate = WHISPER_SAMPLE_RATE
) {
  const numChannels = audioBuffer.numberOfChannels;
  const srcLen = audioBuffer.length;
  const mono = new Float32Array(srcLen);

  for (let c = 0; c < numChannels; c++) {
    const ch = audioBuffer.getChannelData(c);
    for (let i = 0; i < srcLen; i++) {
      mono[i] += ch[i] / numChannels;
    }
  }

  if (audioBuffer.sampleRate === targetSampleRate) {
    return mono;
  }

  const ratio = audioBuffer.sampleRate / targetSampleRate;
  const dstLen = Math.floor(srcLen / ratio);
  const resampled = new Float32Array(dstLen);

  for (let i = 0; i < dstLen; i++) {
    const srcIdx = i * ratio;
    const i0 = Math.floor(srcIdx);
    const i1 = Math.min(i0 + 1, srcLen - 1);
    const frac = srcIdx - i0;
    resampled[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
  }

  return resampled;
}

/**
 * Decodes media audio buffer and resamples to 16 kHz mono Float32Array.
 */
export async function extract16kHzMonoPcm(arrayBuffer, options = {}) {
  const {
    audioDecoder = null,
    targetSampleRate = WHISPER_SAMPLE_RATE,
    signal = null,
  } = options;

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  if (audioDecoder) {
    const decoded = await audioDecoder(arrayBuffer);
    if (decoded instanceof Float32Array) {
      return decoded;
    }
    if (decoded && typeof decoded.getChannelData === "function") {
      return resampleLinearTo16kHzMono(decoded, targetSampleRate);
    }
    return decoded;
  }

  const AudioCtx =
    typeof window !== "undefined" &&
    (window.AudioContext || window.webkitAudioContext);
  if (!AudioCtx) {
    throw new Error("Web Audio API støttes ikke i dette miljøet");
  }

  const ctx = new AudioCtx();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    if (typeof ctx.close === "function") {
      ctx.close().catch(() => {});
    }
  }

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  const OfflineCtx =
    typeof window !== "undefined" &&
    (window.OfflineAudioContext || window.webkitOfflineAudioContext);
  if (OfflineCtx) {
    try {
      const targetLength = Math.ceil(decoded.duration * targetSampleRate);
      const offline = new OfflineCtx(1, targetLength, targetSampleRate);
      const source = offline.createBufferSource();
      source.buffer = decoded;
      source.connect(offline.destination);
      source.start(0);
      const rendered = await offline.startRendering();
      return rendered.getChannelData(0);
    } catch {
      return resampleLinearTo16kHzMono(decoded, targetSampleRate);
    }
  }

  return resampleLinearTo16kHzMono(decoded, targetSampleRate);
}

/**
 * Yields execution to the macrotask queue so the browser can paint UI updates.
 */
export const yieldToMainThread = () =>
  new Promise((resolve) => setTimeout(resolve, 20));

/**
 * Splits a long Float32Array audio buffer into chunks (<= 28s each) at natural speech pauses.
 */
export function splitPcmIntoAudioChunks(
  pcmData,
  sampleRate = WHISPER_SAMPLE_RATE
) {
  const maxChunkSamples = 28 * sampleRate;
  if (!pcmData || pcmData.length <= maxChunkSamples) {
    return [pcmData];
  }

  const chunks = [];
  let offset = 0;
  const totalSamples = pcmData.length;

  while (offset < totalSamples) {
    const remaining = totalSamples - offset;
    if (remaining <= maxChunkSamples) {
      chunks.push(pcmData.subarray(offset));
      break;
    }

    const minSplit = offset + Math.floor(18 * sampleRate);
    const maxSplit = offset + Math.floor(28 * sampleRate);
    const splitIdx = findLowestEnergySplit(
      pcmData,
      minSplit,
      maxSplit,
      sampleRate
    );

    chunks.push(pcmData.subarray(offset, splitIdx));
    offset = splitIdx;
  }

  return chunks;
}

function findLowestEnergySplit(pcmData, minSplit, maxSplit, sampleRate) {
  const windowSize = Math.max(1, Math.floor(0.1 * sampleRate));
  const step = Math.max(1, Math.floor(windowSize / 2));
  let minEnergy = Infinity;
  let bestWindowStart = minSplit;

  for (let w = minSplit; w + windowSize <= maxSplit; w += step) {
    let energy = 0;
    const end = w + windowSize;
    for (let j = w; j < end; j++) {
      energy += pcmData[j] * pcmData[j];
    }
    if (energy < minEnergy) {
      minEnergy = energy;
      bestWindowStart = w;
      if (energy === 0) {
        break;
      }
    }
  }

  return bestWindowStart + Math.floor(windowSize / 2);
}

/**
 * End-to-end transcription from video/audio URL or Dimu ID using NB-Whisper.
 */
export async function transcribeMediaUrl(mediaInput, options = {}) {
  const {
    onProgress = null,
    signal = null,
    fetchFn = undefined,
    transformersLoader = defaultTransformersLoader,
    audioDecoder = null,
    modelId = PRIMARY_WHISPER_MODEL_ID,
    yieldToMainThread: yieldFn = yieldToMainThread,
  } = options;

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  // 1. Fetch media audio
  if (onProgress) {
    onProgress({
      status: "Henter lydspor fra media...",
      progress: 5,
      stage: "fetch",
    });
  }
  const audioBuffer = await fetchMediaAudioBuffer(mediaInput, {
    fetchFn,
    signal,
    onProgress,
  });

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  // 2. Decode and resample to 16 kHz mono Float32Array
  if (onProgress) {
    onProgress({
      status: "Dekoder lyd til 16 kHz...",
      progress: 25,
      stage: "decode",
    });
  }
  const pcmData = await extract16kHzMonoPcm(audioBuffer, {
    audioDecoder,
    targetSampleRate: WHISPER_SAMPLE_RATE,
    signal,
  });

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  // 3. Load or reuse cached ASR pipeline
  if (onProgress) {
    onProgress({
      status: "Laster ned NB-Whisper AI-modell (første gang)...",
      progress: 40,
      stage: "model_load",
    });
  }

  const pipelineInstance = await loadWhisperPipeline(
    modelId,
    transformersLoader,
    signal,
    onProgress
  );

  if (signal?.aborted) {
    throw new Error("Transkripsjon avbrutt");
  }

  // 4. Perform ASR inference on chunks
  const pcmChunks = splitPcmIntoAudioChunks(pcmData, WHISPER_SAMPLE_RATE);
  const chunkTexts = [];

  for (let i = 0; i < pcmChunks.length; i++) {
    if (signal?.aborted) {
      throw new Error("Transkripsjon avbrutt");
    }

    if (onProgress) {
      const status =
        pcmChunks.length > 1
          ? `Transkriberer tale til norsk tekst... (del ${i + 1} av ${pcmChunks.length})`
          : "Transkriberer tale til norsk tekst...";
      const progress =
        pcmChunks.length > 1
          ? 70 + Math.round((i / pcmChunks.length) * 25)
          : 70;
      onProgress({
        status,
        progress,
        stage: "transcribe",
      });
    }

    await yieldFn();

    const output = await pipelineInstance(pcmChunks[i], {
      language: "norwegian",
      task: "transcribe",
      chunk_length_s: 30,
      stride_length_s: 5,
      return_timestamps: false,
    });

    if (signal?.aborted) {
      throw new Error("Transkripsjon avbrutt");
    }

    const trimmed = (output?.text || "").trim();
    if (trimmed) {
      chunkTexts.push(trimmed);
    }

    if (pcmChunks.length > 1 && onProgress) {
      onProgress({
        status: `Transkriberer tale til norsk tekst... (del ${i + 1} av ${pcmChunks.length})`,
        progress: 70 + Math.round(((i + 1) / pcmChunks.length) * 25),
        stage: "transcribe",
        partialText: chunkTexts.join(" "),
      });
    }
  }

  const resultText = chunkTexts.join(" ");

  if (onProgress) {
    onProgress({
      status: "Ferdig!",
      progress: 100,
      stage: "complete",
    });
  }

  return resultText;
}

async function loadWhisperPipeline(
  modelId,
  transformersLoader,
  signal,
  onProgress
) {
  if (cachedPipelines[modelId]) {
    return cachedPipelines[modelId];
  }

  const transformers = await transformersLoader();
  const candidateModels = [
    modelId,
    ...FALLBACK_WHISPER_MODEL_IDS.filter((m) => m !== modelId),
  ];
  let lastError = null;

  for (const mId of candidateModels) {
    if (signal?.aborted) {
      throw new Error("Transkripsjon avbrutt");
    }
    try {
      const pipelineInstance = await transformers.pipeline(
        "automatic-speech-recognition",
        mId,
        {
          progress_callback: (item) => {
            if (
              item?.status === "progress" &&
              typeof item?.progress === "number" &&
              onProgress
            ) {
              onProgress({
                status: `Laster modell: ${item.file || ""} (${Math.round(
                  item.progress
                )} %)`,
                progress: 40 + Math.round((item.progress / 100) * 20),
                stage: "model_load",
              });
            }
          },
        }
      );
      cachedPipelines[mId] = pipelineInstance;
      return pipelineInstance;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Kunne ikke laste talegjenkjenningsmodell");
}
